import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';
import { useRiskAssessment } from '../../contexts/RiskAssessmentContext';
import { getEffectiveScore } from '../../utils/scoreSourceUtils';
import PipelineExportMenu from '../../components/pursuit/PipelineExportMenu';
import PipelineExportHistoryDialog from '../../components/pursuit/PipelineExportHistoryDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Plus, 
  LayoutGrid, 
  List,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Loader2,
  Calendar,
  Archive,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Check,
  X,
  DollarSign,
  Building2,
  Users,
  FileText,
  Radar
} from 'lucide-react';
import { cn } from '../../lib/utils';
import PursuitLimitModal from '../../components/modals/PursuitLimitModal';
import AssignTeamModal from '../../components/pursuit/AssignTeamModal';
import { PhaseTracker } from '../../components/pursuit/PhaseTracker';
import {
  OriginBadge,
  CollaboratorAvatars,
} from '../../components/pursuit/CollaborationBadges';
import { WinLibraryPromptDialog } from '../../components/pursuit/ClientLibraryLinkage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pipeline stages configuration
// v3.18.0 — Strategy column re-introduced now that Strategy-stage screens
// (Win Theme Registry + Section M Evaluation Factors) are shipping. Go
// decisions on Go/No-Go now auto-promote pursuits to STRATEGY (not DRAFTING),
// keeping the federal lifecycle intact end-to-end.
//
// v3.18.9 — WON and LOST collapsed into a single "Won / Lost" lane so the
// board fits cleanly in a 6-column grid (the 7th lane was wrapping to a
// second row at every viewport). The Closed lane has its own in-lane
// segmented filter ('all' | 'won' | 'lost') and each card keeps its own
// emerald/rose outcome chip so visual distinction is preserved.
//
// `statuses` is the array of pursuit.status values that map into each lane.
// For real stages it's a singleton; for CLOSED it includes both WON & LOST.
const PIPELINE_STAGES = [
  { id: 'INTAKE',        label: 'Intake',     color: 'bg-slate-100',  statuses: ['INTAKE'] },
  { id: 'QUALIFICATION', label: 'Go / No-Go', color: 'bg-blue-50',    statuses: ['QUALIFICATION'] },
  { id: 'STRATEGY',      label: 'Strategy',   color: 'bg-violet-50',  statuses: ['STRATEGY'] },
  { id: 'DRAFTING',      label: 'Drafting',   color: 'bg-amber-50',   statuses: ['DRAFTING'] },
  { id: 'SUBMITTED',     label: 'Submitted',  color: 'bg-teal-50',    statuses: ['SUBMITTED'] },
  { id: 'CLOSED',        label: 'Won / Lost', color: 'bg-slate-50',   statuses: ['WON', 'LOST'] },
];

// pWin styling
const getPwinStyle = (pWin, trend) => {
  if (pWin === null || pWin === undefined) {
    return { bg: 'bg-slate-100', text: 'text-slate-400', label: '—' };
  }
  if (pWin >= 70) return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: `${pWin}%` };
  if (pWin >= 50) return { bg: 'bg-amber-100', text: 'text-amber-700', label: `${pWin}%` };
  return { bg: 'bg-red-100', text: 'text-red-700', label: `${pWin}%` };
};

// Risk badge
const getRiskBadge = (risk) => {
  if (!risk) return null;
  const styles = {
    LOW: 'bg-emerald-100 text-emerald-700',
    MEDIUM: 'bg-amber-100 text-amber-700',
    HIGH: 'bg-red-100 text-red-700'
  };
  return styles[risk] || 'bg-slate-100 text-slate-700';
};

// Days remaining styling
const getDaysStyle = (days, urgency) => {
  if (days === null) return { color: 'text-slate-400', label: 'No deadline' };
  if (urgency === 'overdue') return { color: 'text-red-600 font-bold', label: `${Math.abs(days)}d overdue` };
  if (urgency === 'urgent') return { color: 'text-red-600 font-semibold', label: `${days}d remaining` };
  if (urgency === 'warning') return { color: 'text-amber-600', label: `${days}d remaining` };
  return { color: 'text-slate-600', label: `${days}d remaining` };
};

export default function PursuitPipeline() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, authToken } = useAuth();
  const { enterPursuit } = usePursuit();
  
  // Role checks
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const isConsultant = user?.role === ROLES.CONSULTANT;
  const isReviewer = user?.role === ROLES.REVIEWER;
  
  // Data states
  const [pursuits, setPursuits] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, archived: 0, by_status: {} });
  const [creators, setCreators] = useState([]);  // [{org_id, org_name, count, is_self}]
  const [collaborators, setCollaborators] = useState([]);  // same shape; "__none__" = "Just me"
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedOrgId, setSelectedOrgId] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  // v3.18.9 — In-lane filter for the collapsed Won/Lost lane. Lets the
  // user narrow the visible Closed cards without leaving the board.
  // 'all' = both WON and LOST, 'won' = only WON, 'lost' = only LOST.
  const [closedFilter, setClosedFilter] = useState('all');
  // v3.16.0 — pipeline export history dialog
  const [showExportHistory, setShowExportHistory] = useState(false);
  // "Created by" filter (v3.8.0). Server-side filter on `billing_org_id`.
  // 'all' = every pursuit visible to me; otherwise the org_id of a
  // specific creator. Persisted via URL `?createdBy=<orgId>` so refresh
  // and share-link work cleanly.
  const [createdByFilter, setCreatedByFilter] = useState(
    searchParams.get('createdBy') || 'all'
  );
  // "Collaborator" filter (v3.8.1). Independent of "Created by". Filters
  // pursuits to those where the given org is the OTHER party — useful
  // for "show me everything I'm working on with Lionwood, regardless of
  // who created the pursuit." Synthetic value `__none__` means "Just me"
  // (no other org involved). Persisted via URL `?with=<orgId|__none__>`.
  const [collaboratorFilter, setCollaboratorFilter] = useState(
    searchParams.get('with') || 'all'
  );
  const [viewMode, setViewMode] = useState('board');
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  const [selectedPursuit, setSelectedPursuit] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  // v3.12.0 — Win→Library auto-prompt state (set after Mark-as-Won success)
  const [winLibraryPrompt, setWinLibraryPrompt] = useState({ open: false, pursuitId: null, organizationId: null });
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    opportunity_name: '',
    agency_code: '',
    agency_name: '',
    bureau_code: '',
    bureau_name: '',
    estimated_value: '',
    response_deadline: '',
    naics_code: '',
    set_aside_type: '',
    owner_name: '',
    client_org_id: ''  // For consultants to select which client the pursuit is for
  });
  
  // Federal hierarchy states
  const [federalAgencies, setFederalAgencies] = useState([]);
  const [federalBureaus, setFederalBureaus] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [loadingBureaus, setLoadingBureaus] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [organizationsFetched, setOrganizationsFetched] = useState(false);
  
  // Archive form state
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveNotes, setArchiveNotes] = useState('');

  // Consultants must pick a specific client before the pipeline populates —
  // "All Organizations" shows nothing until a client is chosen. (Super-Admins
  // legitimately oversee all, so they're exempt.)
  const needsClientSelection = isConsultant && selectedOrgId === 'all';

  // Fetch data
  const fetchData = useCallback(async () => {
    // Don't load anything until a consultant has selected a client org.
    if (isConsultant && selectedOrgId === 'all') {
      setPursuits([]);
      setStats({});
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const headers = { 'Authorization': `Bearer ${authToken}` };
      
      // Build URL with filters
      let url = `${API_URL}/api/pursuits?include_archived=${includeArchived}`;
      if (selectedOrgId !== 'all') {
        url += `&organization_id=${selectedOrgId}`;
      }
      if (stageFilter !== 'all') {
        // v3.18.9 — 'CLOSED' is a synthetic UI lane that bundles WON + LOST.
        // The backend doesn't know about it, so we omit the status filter
        // and let the client-side board grouping / list filter handle it.
        if (stageFilter !== 'CLOSED') {
          url += `&status=${stageFilter}`;
        }
      }
      if (createdByFilter !== 'all') {
        url += `&created_by=${encodeURIComponent(createdByFilter)}`;
      }
      if (collaboratorFilter !== 'all') {
        url += `&collaborator=${encodeURIComponent(collaboratorFilter)}`;
      }

      // Stats query (mirrors filters)
      let statsUrl = `${API_URL}/api/pursuits/stats`;
      const statsParams = [];
      if (selectedOrgId !== 'all') statsParams.push(`organization_id=${selectedOrgId}`);
      if (createdByFilter !== 'all') statsParams.push(`created_by=${encodeURIComponent(createdByFilter)}`);
      if (collaboratorFilter !== 'all') statsParams.push(`collaborator=${encodeURIComponent(collaboratorFilter)}`);
      if (statsParams.length) statsUrl += `?${statsParams.join('&')}`;

      // Fetch pursuits + stats + creators + collaborators in parallel
      const [pursuitsRes, statsRes, creatorsRes, collaboratorsRes] = await Promise.all([
        fetch(url, { headers }),
        fetch(statsUrl, { headers }),
        fetch(`${API_URL}/api/pursuits/creators`, { headers }),
        fetch(`${API_URL}/api/pursuits/collaborators`, { headers }),
      ]);

      if (!pursuitsRes.ok) throw new Error('Failed to fetch pursuits');

      const pursuitsData = await pursuitsRes.json();
      const statsData = await statsRes.json();

      setPursuits(pursuitsData);
      setStats(statsData);
      if (creatorsRes.ok) {
        setCreators(await creatorsRes.json());
      }
      if (collaboratorsRes.ok) {
        setCollaborators(await collaboratorsRes.json());
      }
    } catch (err) {
      console.error('Error fetching pursuits:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [authToken, selectedOrgId, stageFilter, includeArchived, createdByFilter, collaboratorFilter]);

  // Persist filter selections in URL so refresh + share-link work.
  // Both filters use `replace:true` so flipping them doesn't pollute
  // the back-button stack.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (createdByFilter && createdByFilter !== 'all') {
      next.set('createdBy', createdByFilter);
    } else {
      next.delete('createdBy');
    }
    if (collaboratorFilter && collaboratorFilter !== 'all') {
      next.set('with', collaboratorFilter);
    } else {
      next.delete('with');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdByFilter, collaboratorFilter]);

  // FIX: Dedicated useEffect to fetch organizations - waits for user role to be loaded
  useEffect(() => {
    const fetchOrganizations = async () => {
      // Only fetch for consultants/admins AND only when user is fully loaded
      if (!user?.role || !authToken) return;
      if (user.role !== ROLES.SUPER_ADMIN && user.role !== ROLES.CONSULTANT) return;
      
      setLoadingOrganizations(true);
      try {
        const response = await fetch(`${API_URL}/api/trial/organizations`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (response.ok) {
          const orgsData = await response.json();
          setOrganizations(orgsData);
          
          // Auto-select if only one client exists
          if (orgsData.length === 1) {
            setCreateForm(f => ({ ...f, client_org_id: orgsData[0].id }));
          }
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
      } finally {
        setLoadingOrganizations(false);
        setOrganizationsFetched(true);
      }
    };
    
    fetchOrganizations();
  }, [user?.role, authToken]);

  useEffect(() => {
    if (authToken) {
      fetchData();
    }
  }, [fetchData, authToken]);

  // Real-time sync: Refresh data when tab regains focus or periodically
  useEffect(() => {
    if (!authToken) return;
    
    // Refetch when the user returns to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Periodic polling every 30 seconds for background updates (score changes, etc.)
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    }, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(pollInterval);
    };
  }, [authToken, fetchData]);

  // Fetch federal agencies when modal opens
  useEffect(() => {
    const fetchAgencies = async () => {
      if (!showCreateModal || !authToken) return;

      setLoadingAgencies(true);
      try {
        // Unified Agency Playbook (replaces the legacy
        // /api/federal-agencies/agencies endpoint). Returns
        // { items: [...], total } where each item has both `code`
        // (2-letter internal) and `abbreviation` (3-letter public).
        const response = await fetch(`${API_URL}/api/agencies`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Normalise to the shape the dropdown already uses
          // (`abbreviation` as the dropdown value).
          const items = (data.items || []).map((a) => ({
            abbreviation: a.code,         // 2-letter internal code = the value we persist
            public_abbreviation: a.abbreviation, // 3-letter public — display only
            name: a.name,
          }));
          setFederalAgencies(items);
        }
      } catch (err) {
        console.error('Failed to fetch federal agencies:', err);
      } finally {
        setLoadingAgencies(false);
      }
    };

    fetchAgencies();
  }, [showCreateModal, authToken]);

  // Fetch bureaus when agency changes
  useEffect(() => {
    const fetchBureaus = async () => {
      if (!createForm.agency_code || !authToken) {
        setFederalBureaus([]);
        return;
      }

      setLoadingBureaus(true);
      try {
        const response = await fetch(`${API_URL}/api/agencies/bureaus?agency_code=${createForm.agency_code}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (response.ok) {
          const data = await response.json();
          // Endpoint returns a flat list. Normalise to the shape the
          // existing dropdown code expects (`abbreviation`, `name`).
          const items = (Array.isArray(data) ? data : []).map((b) => ({
            abbreviation: b.code,
            name: b.name,
          }));
          setFederalBureaus(items);
        }
      } catch (err) {
        console.error('Failed to fetch bureaus:', err);
      } finally {
        setLoadingBureaus(false);
      }
    };

    fetchBureaus();
  }, [createForm.agency_code, authToken]);

  // Group pursuits by stage for board view. v3.8.0: client-side origin
  // filtering removed; "Created by" is now applied server-side via the
  // `created_by` query param on /api/pursuits.
  // v3.18.9 — CLOSED is a virtual lane that captures both WON and LOST so
  // the board renders in a clean 6-column grid. Real `pursuit.status`
  // values are unchanged in the DB.
  const pursuitsByStage = useMemo(() => {
    const grouped = {};
    PIPELINE_STAGES.forEach(stage => {
      const allowed = new Set(stage.statuses);
      grouped[stage.id] = pursuits.filter(
        p => allowed.has(p.status) && !p.archived_at
      );
    });
    return grouped;
  }, [pursuits]);

  // Counts for the "Created by" filter chip (v3.8.0).
  // The `creators` array from the server already contains per-creator
  // counts and `is_self`. Compute total active here purely for the
  // "All creators ({n})" label.
  const totalActive = useMemo(
    () => pursuits.filter(p => !p.archived_at).length,
    [pursuits]
  );

  // Check if user can create pursuit
  const checkCanCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trial/gating-status`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLimitInfo(data.pursuits);
        
        if (!data.pursuits.can_create) {
          setShowLimitModal(true);
          return false;
        }
      }
      return true;
    } catch (err) {
      console.error('Error checking pursuit limit:', err);
      return true; // Allow attempt, let backend handle error
    }
  };

  const handleCreateClick = async () => {
    const canCreate = await checkCanCreate();
    if (canCreate) {
      setShowCreateModal(true);
    }
  };

  const handleCreatePursuit = async () => {
    setIsCreating(true);
    
    try {
      const body = {
        opportunity_name: createForm.opportunity_name,
        agency_name: createForm.agency_name,
        agency_code: createForm.agency_code,
        bureau_name: createForm.bureau_name,
        bureau_code: createForm.bureau_code,
        estimated_value: createForm.estimated_value ? parseFloat(createForm.estimated_value.replace(/,/g, '')) : null,
        // v3.10.6: normalize `datetime-local` input to ISO so Pydantic's
        // datetime parser gets a deterministic representation regardless
        // of the user's browser/OS locale. `datetime-local` returns
        // `"YYYY-MM-DDTHH:mm"` (no seconds, no timezone) which Pydantic
        // accepts but then treats as naive — `new Date(...).toISOString()`
        // produces a UTC-anchored string so display math never drifts.
        response_deadline: createForm.response_deadline
          ? new Date(createForm.response_deadline).toISOString()
          : null,
        naics_code: createForm.naics_code,
        set_aside_type: createForm.set_aside_type,
        owner_name: createForm.owner_name
      };
      
      // Determine organization_id:
      // 1. If consultant selected a client org, use that
      // 2. Else if a filter is selected, use that
      // 3. Else default (backend will use user's org)
      let targetOrgId = createForm.client_org_id || null;
      if (!targetOrgId && selectedOrgId !== 'all') {
        targetOrgId = selectedOrgId;
      }
      
      const url = targetOrgId 
        ? `${API_URL}/api/pursuits?organization_id=${targetOrgId}`
        : `${API_URL}/api/pursuits`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.error === 'PURSUIT_LIMIT_REACHED') {
          setLimitInfo(data);
          setShowCreateModal(false);
          setShowLimitModal(true);
          return;
        }
        throw new Error(data.detail || 'Failed to create pursuit');
      }
      
      setShowCreateModal(false);
      setCreateForm({
        opportunity_name: '',
        agency_code: '',
        agency_name: '',
        bureau_code: '',
        bureau_name: '',
        estimated_value: '',
        response_deadline: '',
        naics_code: '',
        set_aside_type: '',
        owner_name: '',
        client_org_id: ''
      });
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAssignTeamClick = (pursuit, e) => {
    e.stopPropagation();
    setSelectedPursuit(pursuit);
    setShowAssignTeamModal(true);
  };

  const handleArchiveClick = (pursuit, e) => {
    e.stopPropagation();
    setSelectedPursuit(pursuit);
    setArchiveReason('');
    setArchiveNotes('');
    setShowArchiveModal(true);
  };

  const handleArchivePursuit = async () => {
    if (!selectedPursuit || !archiveReason) return;
    setIsArchiving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${selectedPursuit.id}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          archive_reason: archiveReason,
          archive_notes: archiveNotes
        })
      });
      
      if (!response.ok) throw new Error('Failed to archive pursuit');
      
      setShowArchiveModal(false);
      setSelectedPursuit(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsArchiving(false);
    }
  };

  // v3.11.25 — Mark a SUBMITTED pursuit as WON or LOST. Single-click
  // hover action on the card; confirms via window.confirm + optional
  // notes prompt. Does NOT archive — outcome is a status transition,
  // archive is a separate slot-management concern.
  const handleMarkOutcome = async (pursuit, outcome, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    const verb = outcome === 'WON' ? 'WON' : 'LOST';
    if (!window.confirm(
      `Mark "${pursuit.opportunity_name || pursuit.title || 'this pursuit'}" as ${verb}?\n\n` +
      `This records the outcome and moves the card to the ${outcome === 'WON' ? 'Won' : 'Lost'} column.`
    )) return;
    const notes = window.prompt(
      `Optional notes for this ${verb} outcome (leave blank to skip):`,
      ''
    );
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuit.id}/mark-outcome`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ outcome, notes: notes || null })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.detail || `Failed to mark as ${verb}`);
      }
      window.dispatchEvent(new CustomEvent('pursuit-status-changed', {
        detail: { pursuitId: pursuit.id, newStatus: outcome }
      }));
      fetchData();
      // v3.12.0 — Win→Library auto-prompt: when a pursuit is marked WON,
      // surface a one-time prompt to promote the submitted documents to
      // the Winning Proposals card on the Client Profile.
      if (outcome === 'WON') {
        setWinLibraryPrompt({
          open: true,
          pursuitId: pursuit.id,
          organizationId: pursuit.organization_id,
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePursuitClick = (pursuit) => {
    const pursuitData = {
      id: pursuit.id,
      title: pursuit.opportunity_name,
      rfpNumber: pursuit.solicitation_number || pursuit.agency_code || 'N/A',
      client: pursuit.organization_id,
      pWinScore: pursuit.pwin_score,
      dueDate: pursuit.response_deadline,
      notice_type: pursuit.notice_type,  // v3.13.0 — sidebar gates Capability Statement on this
    };
    enterPursuit(pursuitData);
    // Reviewers go directly to Go/No-Go, others go to Intake
    const targetPage = isReviewer ? 'go-no-go' : 'intake';
    navigate(`/${workspaceId}/pursuit/${pursuit.id}/${targetPage}`);
  };

  // Pursuit Card Component
  // v3.18.9 — Optional `showOutcomeChip` paints a tiny outcome ribbon in
  // the top-right corner for cards rendered inside the collapsed
  // Won/Lost lane. Visual cue stays clear when both outcomes coexist.
  const PursuitCard = ({ pursuit, showOutcomeChip = false }) => {
    const effectiveScore = getEffectiveScore(pursuit).score;
    const pwinStyle = getPwinStyle(effectiveScore, pursuit.pwin_trend);
    const daysStyle = getDaysStyle(pursuit.days_remaining, pursuit.deadline_urgency);
    const riskBadge = getRiskBadge(pursuit.risk_level);
    const outcomeChip = showOutcomeChip && (pursuit.status === 'WON' || pursuit.status === 'LOST') ? (
      <span
        className={cn(
          'absolute top-2 right-2 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide',
          pursuit.status === 'WON'
            ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
            : 'bg-rose-100 text-rose-700 ring-1 ring-rose-200'
        )}
        data-testid={`outcome-chip-${pursuit.id}`}
        title={pursuit.status === 'WON' ? 'Marked as Won' : 'Marked as Lost'}
      >
        {pursuit.status}
      </span>
    ) : null;
    
    return (
      <Card 
        className={cn(
          "border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group relative overflow-hidden",
          pursuit.archived_at && "opacity-60"
        )}
        onClick={() => handlePursuitClick(pursuit)}
        data-testid={`pursuit-card-${pursuit.id}`}
      >
        <CardContent className="p-4 min-w-0">
          {outcomeChip}
          {/* Client Name - visible for consultants/admins when viewing multiple orgs */}
          {(isSuperAdmin || isConsultant) && (
            <div className={cn(
              "flex items-center gap-1 mb-2 text-xs px-2 py-1 rounded-md min-w-0",
              pursuit.client_name ? "text-teal-700 bg-teal-50" : "text-amber-700 bg-amber-50"
            )} data-testid={`client-name-${pursuit.id}`}>
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium truncate min-w-0">{pursuit.client_name || 'Client Not Specified'}</span>
            </div>
          )}

          {/* Origin badge — shows "Invited by client" or "Set up by consultant"
              when the pursuit was authored by an external org. Hidden for
              SELF_CREATED to keep cards quiet. */}
          {pursuit.origin === 'INVITED' && (
            <div className="mb-2">
              <OriginBadge pursuit={pursuit} viewerRole={user?.role} />
            </div>
          )}

          {/* Top: Agency Code + Title */}
          <div className="flex items-start gap-2 mb-3 min-w-0">
            {pursuit.agency_code && (
              <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded bg-slate-600 flex-shrink-0">
                {pursuit.agency_code}
              </span>
            )}
            <h4 className="font-medium text-slate-800 text-sm leading-tight group-hover:text-blue-600 transition-colors flex-1 min-w-0 break-words line-clamp-3">
              {pursuit.opportunity_name}
            </h4>
          </div>

          {/* Value Display */}
          {pursuit.estimated_value_formatted && (
            <div className="flex items-center gap-1 mb-2 text-sm text-emerald-700 font-semibold min-w-0">
              <DollarSign className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{pursuit.estimated_value_formatted.replace('$', '')}</span>
            </div>
          )}

          {/* Fit Score + Risk Row */}
          <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className={cn('px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap', pwinStyle.bg, pwinStyle.text)}>
                Fit: {pwinStyle.label}
              </span>
              {pursuit.pwin_trend === 'up' && <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
              {pursuit.pwin_trend === 'down' && <TrendingDown className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
            </div>
            {riskBadge && (
              <span className={cn('px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap flex-shrink-0', riskBadge)}>
                {pursuit.risk_level}
              </span>
            )}
          </div>

          {/* FAR Lifecycle Phase Badge (Compact) */}
          <div className="mb-2">
            <PhaseTracker
              pursuitId={pursuit.id}
              currentPhase={pursuit.procurement_phase}
              authToken={authToken}
              compact={true}
            />
          </div>

          {/* Deadline */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100 min-w-0">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className={cn('text-xs truncate', daysStyle.color)}>
                {daysStyle.label}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Collaborator avatar stack — every org with access except the
                  viewer's own org. Static (non-hovering) so it's always visible. */}
              <CollaboratorAvatars
                pursuit={pursuit}
                viewerOrgId={user?.organizationId}
                max={3}
              />

              {/* Hover-only action buttons (Mark Won/Lost / Assign / Archive) */}
              {!pursuit.archived_at && !isReviewer && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* v3.11.25 — Outcome quick-actions only on SUBMITTED cards.
                      Card-only UX (no separate dropdown); confirms via
                      window.confirm. */}
                  {pursuit.status === 'SUBMITTED' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => handleMarkOutcome(pursuit, 'WON', e)}
                        data-testid={`mark-won-${pursuit.id}`}
                        title="Mark as Won"
                      >
                        <Check className="w-3.5 h-3.5 text-slate-400 hover:text-emerald-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => handleMarkOutcome(pursuit, 'LOST', e)}
                        data-testid={`mark-lost-${pursuit.id}`}
                        title="Mark as Lost"
                      >
                        <X className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => handleAssignTeamClick(pursuit, e)}
                    data-testid={`assign-team-${pursuit.id}`}
                    title="Assign Team"
                  >
                    <Users className="w-3.5 h-3.5 text-slate-400 hover:text-emerald-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => handleArchiveClick(pursuit, e)}
                    data-testid={`archive-${pursuit.id}`}
                    title="Archive"
                  >
                    <Archive className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* v3.18.22 / v3.18.23 — Sentinel attribution badge: shows that
              this pursuit was claimed from a Sentinel match and indicates
              the source (SAM.gov or GSA eBuy) plus who pulled the trigger. */}
          {pursuit.added_via_sentinel && (
            <div className="mt-2 flex items-center gap-1.5">
              <Badge
                variant="outline"
                className={`text-[10px] flex items-center gap-1 ${pursuit.sentinel_source === 'ebuy.gsa.gov' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-cyan-200 bg-cyan-50 text-cyan-800'}`}
                data-testid={`sentinel-badge-${pursuit.id}`}
                title={`Added from ${pursuit.sentinel_source === 'ebuy.gsa.gov' ? 'GSA eBuy' : 'SAM.gov'} Sentinel by ${pursuit.added_by_org_type === 'CONSULTANT' ? 'consultant' : pursuit.added_by_org_type === 'CONTRACTOR' ? 'contractor' : 'admin'}`}
              >
                <Radar className="w-3 h-3" />
                {pursuit.sentinel_source === 'ebuy.gsa.gov' ? 'eBuy' : 'SAM.gov'}
                {pursuit.added_by_org_type === 'CONSULTANT' && (
                  <span className="opacity-70">· consultant</span>
                )}
                {pursuit.added_by_org_type === 'CONTRACTOR' && (
                  <span className="opacity-70">· contractor</span>
                )}
              </Badge>
            </div>
          )}

          {/* Owner */}
          {(pursuit.owner_name || pursuit.bureau_name) && (
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              {pursuit.owner_name && <span data-testid={`card-owner-${pursuit.id}`}>Owner: {pursuit.owner_name}</span>}
              {pursuit.bureau_name && (
                <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-500">
                  {pursuit.bureau_name}
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="pursuit-pipeline" className="min-h-screen bg-slate-50">
      {/* Page Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pursuit Pipeline
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {stats.active} active pursuit{stats.active !== 1 ? 's' : ''} 
              {stats.archived > 0 && ` · ${stats.archived} archived`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Button variant="outline" onClick={fetchData} disabled={isLoading}>
              <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
              Refresh
            </Button>
            {!isReviewer && (
              <Button 
                className="bg-teal-700 hover:bg-teal-800"
                onClick={handleCreateClick}
                data-testid="create-pursuit-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Pursuit
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content — edge-to-edge body (kanban needs the width on >1280px). */}
      <div className="p-8 space-y-6">
        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Organization Filter (for Consultants/Admins) */}
          {(isConsultant || isSuperAdmin) && organizations.length > 0 && (
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-400" />
              <Select value={selectedOrgId} onValueChange={(v) => setSelectedOrgId(v)}>
                <SelectTrigger className="w-48" data-testid="org-filter">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Stage Filter */}
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-40" data-testid="stage-filter">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {PIPELINE_STAGES.map(stage => (
                <SelectItem key={stage.id} value={stage.id}>
                  {stage.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* "Created by" Filter (v3.8.0) — slice the pipeline by who
              created each pursuit. Renders dynamically based on what
              creator orgs the user actually has visible. Hidden when
              there's only one creator (or none) so we don't show a
              dropdown with a single option. */}
          {creators.length > 1 && (
            <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
              <SelectTrigger className="w-56" data-testid="created-by-filter">
                <SelectValue placeholder="All creators" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="created-by-filter-all">
                  All creators ({totalActive})
                </SelectItem>
                {creators.map((c) => (
                  <SelectItem
                    key={c.org_id}
                    value={c.org_id}
                    data-testid={`created-by-filter-${c.is_self ? 'self' : c.org_id}`}
                  >
                    {c.is_self ? 'Me' : c.org_name} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* "Collaborator" Filter (v3.8.1) — independent of "Created by".
              Slices the pipeline by who the user is WORKING WITH on each
              pursuit (regardless of who created it). Answers "show me
              everything I'm doing with Lionwood" for a contractor and
              "show me everything for client MNG Group" for a consultancy.
              The synthetic "Just me" entry covers solo pursuits.
              Hidden when ≤1 distinct collaborator entry. */}
          {collaborators.length > 1 && (
            <Select
              value={collaboratorFilter}
              onValueChange={setCollaboratorFilter}
            >
              <SelectTrigger className="w-56" data-testid="collaborator-filter">
                <SelectValue placeholder="Working with" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="collaborator-filter-all">
                  All collaborators ({totalActive})
                </SelectItem>
                {collaborators.map((c) => (
                  <SelectItem
                    key={c.org_id}
                    value={c.org_id}
                    data-testid={`collaborator-filter-${c.org_id === '__none__' ? 'none' : c.org_id}`}
                  >
                    {c.org_name} ({c.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* View Toggle */}
          <div className="flex items-center border rounded-lg p-1 bg-slate-100">
            <Button
              variant={viewMode === 'board' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('board')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>

          {/* Include Archived Toggle */}
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show Archived
          </label>

          {/* v3.16.0 — Export menu (PDF/XLSX/CSV) */}
          <div className="ml-auto">
            <PipelineExportMenu
              filters={{
                organization_id: selectedOrgId,
                stage: stageFilter,
                include_archived: includeArchived,
              }}
              rowCount={pursuits.filter(p => !p.archived_at || includeArchived).length}
              onShowHistory={() => setShowExportHistory(true)}
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Loading State */}
        {needsClientSelection ? (
          <Card className="border-dashed border-2 border-teal-200" data-testid="pipeline-select-client-prompt">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-teal-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a client organization</h3>
              <p className="text-slate-500">
                Choose a client from the organization filter above to view its pursuit pipeline.
              </p>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : pursuits.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">
                {isReviewer ? 'No assigned pursuits' : 'No pursuits yet'}
              </h3>
              <p className="text-slate-500 mb-4">
                {isReviewer 
                  ? 'You will see pursuits here once you are assigned to review them'
                  : 'Create your first pursuit to start tracking opportunities'
                }
              </p>
              {!isReviewer && (
                <Button onClick={handleCreateClick} className="bg-teal-700 hover:bg-teal-800">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Pursuit
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Board View */
          viewMode === 'board' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="pipeline-board">
              {PIPELINE_STAGES.map(stage => {
                const isClosed = stage.id === 'CLOSED';
                // Apply in-lane Won/Lost filter only on the Closed lane.
                const laneCards = (pursuitsByStage[stage.id] || []).filter(p => {
                  if (!isClosed || closedFilter === 'all') return true;
                  if (closedFilter === 'won') return p.status === 'WON';
                  if (closedFilter === 'lost') return p.status === 'LOST';
                  return true;
                });
                return (
                  <div
                    key={stage.id}
                    className={cn('rounded-lg p-3 min-w-0', stage.color)}
                    data-testid={`pipeline-lane-${stage.id}`}
                  >
                    <div className="flex items-center justify-between mb-3 gap-2 min-w-0">
                      <h3 className="font-semibold text-slate-700 text-sm truncate">{stage.label}</h3>
                      <Badge variant="secondary" className="text-xs flex-shrink-0">
                        {laneCards.length}
                      </Badge>
                    </div>

                    {/* v3.18.9 — In-lane Won/Lost segmented filter. Only renders
                        in the Closed lane and only when there's at least one
                        card to filter; otherwise the empty lane stays calm. */}
                    {isClosed && (pursuitsByStage[stage.id] || []).length > 0 && (
                      <div
                        className="flex items-center gap-1 mb-3 p-0.5 bg-white/70 border border-slate-200 rounded-md text-[11px]"
                        role="tablist"
                        aria-label="Filter Won/Lost"
                        data-testid="closed-lane-filter"
                      >
                        {[
                          { v: 'all',  label: 'All',  tone: 'text-slate-700' },
                          { v: 'won',  label: 'Won',  tone: 'text-emerald-700' },
                          { v: 'lost', label: 'Lost', tone: 'text-rose-700' },
                        ].map(opt => (
                          <button
                            key={opt.v}
                            type="button"
                            role="tab"
                            aria-selected={closedFilter === opt.v}
                            onClick={() => setClosedFilter(opt.v)}
                            className={cn(
                              'flex-1 px-2 py-1 rounded text-center font-medium transition-colors',
                              closedFilter === opt.v
                                ? `bg-white shadow-sm ${opt.tone}`
                                : 'text-slate-500 hover:text-slate-700'
                            )}
                            data-testid={`closed-filter-${opt.v}`}
                            title={
                              opt.v === 'all'
                                ? 'Show both Won and Lost'
                                : opt.v === 'won'
                                ? 'Show only Won'
                                : 'Show only Lost'
                            }
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      {laneCards.map(pursuit => (
                        <PursuitCard
                          key={pursuit.id}
                          pursuit={pursuit}
                          showOutcomeChip={isClosed}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* List View */
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Opportunity</th>
                      {(isSuperAdmin || isConsultant) && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Agency</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Value</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Deadline</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pursuits.filter(p => {
                      if (!includeArchived && p.archived_at) return false;
                      // v3.18.9 — synthetic CLOSED filter: keep only WON/LOST
                      // since the backend doesn't know about CLOSED.
                      if (stageFilter === 'CLOSED' && p.status !== 'WON' && p.status !== 'LOST') return false;
                      return true;
                    }).map(pursuit => {
                      // v3.11.22 — Removed phantom `matchOrigin(p)` reference that
                      // crashed the list view (`ReferenceError: matchOrigin is not
                      // defined`). Origin filtering moved server-side in v3.8.0
                      // (see comment on `pursuitsByStage`); the helper was deleted
                      // but this list-view filter line wasn't migrated, so the
                      // first click on the List-view toggle threw.
                      const pwinStyle = getPwinStyle(getEffectiveScore(pursuit).score);
                      const daysStyle = getDaysStyle(pursuit.days_remaining, pursuit.deadline_urgency);
                      return (
                        <tr 
                          key={pursuit.id} 
                          className={cn("border-b hover:bg-slate-50 cursor-pointer", pursuit.archived_at && "opacity-60")}
                          onClick={() => handlePursuitClick(pursuit)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-slate-800 flex items-center gap-2">
                                  <span className="truncate">{pursuit.opportunity_name}</span>
                                  {pursuit.origin === 'INVITED' && (
                                    <OriginBadge pursuit={pursuit} viewerRole={user?.role} />
                                  )}
                                </p>
                                <p className="text-xs text-slate-500">{pursuit.solicitation_number}</p>
                              </div>
                              <CollaboratorAvatars
                                pursuit={pursuit}
                                viewerOrgId={user?.organizationId}
                                max={3}
                              />
                            </div>
                          </td>
                          {(isSuperAdmin || isConsultant) && (
                            <td className="px-4 py-3" data-testid={`list-client-name-${pursuit.id}`}>
                              <div className="flex items-center gap-1 text-xs text-teal-700">
                                <Building2 className="w-3 h-3" />
                                <span className="font-medium">{pursuit.client_name || '-'}</span>
                              </div>
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <Badge variant="secondary">{pursuit.agency_code || '-'}</Badge>
                          </td>
                          <td className="px-4 py-3 font-semibold text-emerald-700">
                            {pursuit.estimated_value_formatted || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge>{pursuit.status}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn('px-2 py-0.5 rounded text-xs font-medium', pwinStyle.bg, pwinStyle.text)}>
                              {pwinStyle.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn('text-sm', daysStyle.color)}>{daysStyle.label}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!pursuit.archived_at && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleArchiveClick(pursuit, e)}
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </CardContent>
            </Card>
          )
        )}
      </div>

      {/* Create Pursuit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle>Create New Pursuit</DialogTitle>
            <DialogDescription>
              Enter the opportunity details to start tracking this pursuit
            </DialogDescription>
          </DialogHeader>

          {(() => {
            // When the consultant/super-admin has no clients yet, the rest
            // of the form is unusable (Create button is disabled). Render
            // ONLY the empty-state CTA — keeps the modal compact and
            // avoids the long-form scroll problem reported in QA.
            const noClients = (isConsultant || isSuperAdmin)
              && !loadingOrganizations
              && organizationsFetched
              && organizations.length === 0;
            return noClients;
          })() ? (
            <>
              <div className="px-6 py-4">
                <div className="p-4 border border-dashed border-amber-300 rounded-lg bg-amber-50">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">No clients in your directory</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Add a client organization to your directory before creating a pursuit.
                      </p>
                      <Link
                        to={`/${workspaceId || 'admin'}/clients`}
                        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors"
                        onClick={() => setShowCreateModal(false)}
                        data-testid="add-client-link"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Client to Directory
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="px-6 pb-6 flex-shrink-0">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
                {/* Client Organization Selector - Required for Consultants/Super Admins */}
                {(isConsultant || isSuperAdmin) && (
                  <div className="space-y-2">
                    <Label htmlFor="client_org">Client Organization *</Label>

                    {/* Loading state */}
                    {loadingOrganizations && (
                      <div className="flex items-center gap-2 p-3 border rounded-md bg-slate-50">
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                        <span className="text-sm text-slate-500">Loading client directory...</span>
                      </div>
                    )}

                    {/* Normal state - Has clients */}
                    {!loadingOrganizations && organizations.length >= 1 && (
                      <>
                        <Select
                          value={createForm.client_org_id}
                          onValueChange={(v) => setCreateForm(f => ({ ...f, client_org_id: v }))}
                        >
                          <SelectTrigger id="client_org" data-testid="pursuit-client-select">
                            <SelectValue placeholder="Select client organization..." />
                          </SelectTrigger>
                          <SelectContent>
                        {organizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {org.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Which client is this opportunity for?
                    </p>
                  </>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="opportunity_name">Opportunity Name *</Label>
              <Input
                id="opportunity_name"
                placeholder="e.g., IT Modernization Support Services"
                value={createForm.opportunity_name}
                onChange={(e) => setCreateForm(f => ({ ...f, opportunity_name: e.target.value }))}
                data-testid="pursuit-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="agency_code">Agency *</Label>
                <Select 
                  value={createForm.agency_code} 
                  onValueChange={(v) => {
                    const agency = federalAgencies.find(a => a.abbreviation === v);
                    setCreateForm(f => ({ 
                      ...f, 
                      agency_code: v,
                      agency_name: agency?.name || '',
                      bureau_code: '',
                      bureau_name: ''
                    }));
                  }}
                >
                  <SelectTrigger id="agency_code" data-testid="pursuit-agency-select">
                    {loadingAgencies ? (
                      <span className="flex items-center gap-2 text-slate-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      <SelectValue placeholder="Select agency..." />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {federalAgencies.map(agency => (
                      <SelectItem key={agency.abbreviation} value={agency.abbreviation}>
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] px-1 py-0 font-mono">
                            {/* Dual-badge: 2-letter CODE always shown,
                                 3-letter ABBRV appended after a pipe when
                                 present. Preserves the visual link to bureau
                                 codes which all share the CODE prefix. */}
                            {agency.public_abbreviation
                              ? `${agency.abbreviation} | ${agency.public_abbreviation}`
                              : agency.abbreviation}
                          </Badge>
                          <span className="truncate max-w-[200px]">{agency.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bureau_code">Bureau (Optional)</Label>
                <Select 
                  value={createForm.bureau_code} 
                  onValueChange={(v) => {
                    const bureau = federalBureaus.find(b => b.abbreviation === v);
                    setCreateForm(f => ({ 
                      ...f, 
                      bureau_code: v,
                      bureau_name: bureau?.name || ''
                    }));
                  }}
                  disabled={!createForm.agency_code || loadingBureaus}
                >
                  <SelectTrigger id="bureau_code" data-testid="pursuit-bureau-select">
                    {loadingBureaus ? (
                      <span className="flex items-center gap-2 text-slate-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Loading...
                      </span>
                    ) : !createForm.agency_code ? (
                      <span className="text-slate-400">Select agency first</span>
                    ) : (
                      <SelectValue placeholder="Select bureau..." />
                    )}
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {federalBureaus.map(bureau => (
                      <SelectItem key={bureau.abbreviation} value={bureau.abbreviation}>
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {bureau.abbreviation}
                          </Badge>
                          <span className="truncate max-w-[200px]">{bureau.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                    {federalBureaus.length === 0 && !loadingBureaus && createForm.agency_code && (
                      <div className="px-2 py-1 text-xs text-slate-500">No bureaus for this agency</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated_value">Estimated Value ($)</Label>
                <Input
                  id="estimated_value"
                  placeholder="e.g., 5,000,000"
                  value={createForm.estimated_value}
                  onChange={(e) => setCreateForm(f => ({ ...f, estimated_value: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="response_deadline">Response Deadline</Label>
                <Input
                  id="response_deadline"
                  type="datetime-local"
                  value={createForm.response_deadline}
                  onChange={(e) => setCreateForm(f => ({ ...f, response_deadline: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="naics_code">NAICS Code</Label>
                <Input
                  id="naics_code"
                  placeholder="e.g., 541512"
                  value={createForm.naics_code}
                  onChange={(e) => setCreateForm(f => ({ ...f, naics_code: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="set_aside_type">Set-Aside</Label>
                <Select 
                  value={createForm.set_aside_type} 
                  onValueChange={(v) => setCreateForm(f => ({ ...f, set_aside_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Full & Open</SelectItem>
                    <SelectItem value="SB">Small Business</SelectItem>
                    <SelectItem value="8A">SBA 8(a)</SelectItem>
                    <SelectItem value="WOSB">WOSB</SelectItem>
                    <SelectItem value="SDVOSB">SDVOSB</SelectItem>
                    <SelectItem value="HUBZONE">HUBZone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="owner_name">Capture Manager</Label>
              <Input
                id="owner_name"
                placeholder="e.g., Sarah Chen"
                value={createForm.owner_name}
                onChange={(e) => setCreateForm(f => ({ ...f, owner_name: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter className="px-6 pb-6 pt-2 flex-shrink-0 border-t bg-white">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-teal-700 hover:bg-teal-800"
              onClick={handleCreatePursuit}
              disabled={
                isCreating || 
                !createForm.opportunity_name || 
                !createForm.agency_code ||
                ((isConsultant || isSuperAdmin) && organizations.length === 0) ||
                ((isConsultant || isSuperAdmin) && organizations.length >= 1 && !createForm.client_org_id)
              }
              data-testid="confirm-create-pursuit"
            >
              {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Pursuit
            </Button>
          </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Modal */}
      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Pursuit</DialogTitle>
            <DialogDescription>
              Archived pursuits don't count toward your active pursuit limit
            </DialogDescription>
          </DialogHeader>
          
          {selectedPursuit && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium">{selectedPursuit.opportunity_name}</p>
                <p className="text-sm text-slate-500">{selectedPursuit.agency_code}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Archive Reason *</Label>
                <Select value={archiveReason} onValueChange={setArchiveReason}>
                  <SelectTrigger data-testid="archive-reason-select">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WON">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        Won
                      </div>
                    </SelectItem>
                    <SelectItem value="LOST">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-500" />
                        Lost
                      </div>
                    </SelectItem>
                    <SelectItem value="NO_BID">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        No Bid
                      </div>
                    </SelectItem>
                    <SelectItem value="CANCELLED">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-slate-500" />
                        Cancelled
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add any notes about the outcome..."
                  value={archiveNotes}
                  onChange={(e) => setArchiveNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleArchivePursuit}
              disabled={isArchiving || !archiveReason}
              data-testid="confirm-archive-btn"
            >
              {isArchiving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Archive Pursuit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pursuit Limit Modal */}
      <PursuitLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitInfo={limitInfo}
        onRequestSlot={() => {
          setShowLimitModal(false);
          // Could navigate to contact page or show request form
        }}
        onRequestUpgrade={() => {
          setShowLimitModal(false);
          navigate('/pricing');
        }}
      />

      {/* Assign Team Modal */}
      <AssignTeamModal
        isOpen={showAssignTeamModal}
        onClose={() => setShowAssignTeamModal(false)}
        pursuit={selectedPursuit}
        onAssignmentChange={fetchData}
      />

      {/* v3.12.0 — Win→Library auto-prompt: appears after Mark-as-Won */}
      <WinLibraryPromptDialog
        open={winLibraryPrompt.open}
        onOpenChange={(o) => setWinLibraryPrompt({ ...winLibraryPrompt, open: o })}
        pursuitId={winLibraryPrompt.pursuitId}
        organizationId={winLibraryPrompt.organizationId}
        authToken={authToken}
        onCompleted={() => fetchData()}
      />

      {/* v3.16.0 — Pipeline Export History */}
      <PipelineExportHistoryDialog
        open={showExportHistory}
        onOpenChange={setShowExportHistory}
      />
    </div>
  );
}
